import { useQueryClient } from '@tanstack/react-query';
import { FormikHelpers } from 'formik';
import { AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { useMemo, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { FaBirthdayCake, FaUserAlt } from 'react-icons/fa';
import { FaBan, FaLinkedin, FaLocationDot } from 'react-icons/fa6';
import { IoCall } from 'react-icons/io5';
import { MdEmail, MdVerified } from 'react-icons/md';
import { TfiReload } from 'react-icons/tfi';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useAssociateRequestActions from '../../../hooks/APIHooks/users/useAssociateRequestActions';
import useProfileActions from '../../../hooks/APIHooks/users/useProfileActions';
import { Associate } from '../../../types/associates';
import { showFullName, showPicture } from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AssociateRequestExperience from '../../AssociateRequestsComps/AssociateRequestExperience';
import AssociateRequestInfo from '../../AssociateRequestsComps/AssociateRequestInfo';
import AcademiaSectionWrapper from '../AcademiaSectionWrapper';
import SkillBadge from '../Badges/SkillBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaModalLoading from '../Loadings/AcademiaModalLoading';
import ApplicationConsentModal from './ApplicationConsentModal';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const AssociateRequestDetailsModal = ({
  associate,
  handleModalClose,
  isReadOnly,
}: {
  associate: Associate;
  handleModalClose: () => void;
  isReadOnly?: boolean;
}) => {
  // destructuring the associate object here
  const {
    id,
    username,
    first_name,
    middle_name,
    last_name,
    email,
    country_name,
    iso_code,
    is_view,
    profile_picture,
    last_login_date,
    user_profile_id,
  } = associate || {};

  // integration of react hooks here
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [requestResubmission, setRequestResubmission] =
    useState<boolean>(false);
  const [isRejecting, setIsRejecting] = useState<boolean>(false);

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // integration of custom API hooks here
  const { updateAssociateStatusHandler, updateAssociateViewStatusHandler } =
    useAssociateRequestActions('');
  const { getUserProfileByIdHandler } = useProfileActions(user_profile_id);

  console.log('ASSOCIATE PROFILE: ', getUserProfileByIdHandler.data?.data);

  // destructuring the profile & skills object here
  const {
    address,
    province_name,
    city_name,
    postal_code,
    isd_code,
    phone,
    per_hour_rate,
    years_of_experience,
    languages_spoken,
    bio,
    date_of_birth,
    linkedin_profile,
    user_expertise,
    hst_no,
    license_number,
    business_reg_no,
  } = getUserProfileByIdHandler.data?.data || {};

  // calculating applicant's age here
  const today = moment();
  const DOB = moment(date_of_birth);

  const age = today.diff(DOB, 'years');

  const splittedLinkedinUrl = linkedin_profile
    ? linkedin_profile?.split('/')
    : [];

  console.log('USER PROFILE: ', splittedLinkedinUrl?.length);

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // updating state of application when admin/associate reviews the application
  useMemo(() => {
    if (!is_view) {
      updateAssociateViewStatusHandler
        .mutateAsync({
          associateId: id,
          body: {
            is_view: true,
            updated_by: currentUser?.username as string,
          },
        })
        .then((res) => {
          if (res.is_success) {
            queryClient.invalidateQueries({
              queryKey: ['associate-request-list'],
            });
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_view, currentUser, id]);

  // handler function to handle changing applicant status declared here
  const changeApplicantStatusHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      if (associate.status) {
        await updateAssociateStatusHandler
          .mutateAsync({
            associateId: associate.id as number,
            body: {
              user: associate.id,
              reviewed_by: currentUser?.username as string,
              comments: values.comments,
              status: values.status,
              is_resubmit: false,
              updated_by: currentUser?.username as string,
            },
          })
          .then((res) => {
            if (res.is_success) {
              toast.success(
                values.status === 'resubmit'
                  ? 'Resubmission Requested!!'
                  : `Associate ${
                      values.status === 'approved' ? 'Approved' : 'Rejected'
                    }!!`
              );

              setIsApproving(false);
              setIsRejecting(false);
              handleModalClose();

              queryClient.invalidateQueries({
                queryKey: ['associate-request-list'],
              });
            } else {
              toast.error(
                res.message || values.status === 'resubmit'
                  ? 'Resubmission Request Failed!!'
                  : `${
                      values.status === 'approved' ? 'Approved' : 'Rejected'
                    } Failed!!`
              );
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering associate request details modal component here
  return (
    <>
      <ParentModal onClick={handleModalClose} customWidthClass='md:w-[750px]'>
        {getUserProfileByIdHandler.isLoading ? (
          <AcademiaModalLoading />
        ) : getUserProfileByIdHandler.isSuccess ? (
          <>
            <ModalTitle
              titleText={`${isReadOnly ? 'Associate' : 'Applicant'} Details`}
            />

            <div className='border-t-2 border-dashed border-base-dark-blue pt-2'>
              <div className='flex flex-col md:flex-row items-center justify-between gap-2'>
                <div>
                  <h3 className='text-3xl font-medium text-base-dark-blue'>
                    {showFullName(first_name, middle_name, last_name)}
                  </h3>

                  {isReadOnly ? (
                    <p className='font-semibold text-base-cyan italic text-center md:text-left'>
                      @{username}
                    </p>
                  ) : null}
                </div>

                <div>
                  <p className='md:text-xl text-base-cyan text-center md:text-right'>
                    Age: <span className='font-semibold'>{age || 'N/A'}</span>
                  </p>

                  <span className='text-xs text-base-light-blue'>
                    Last Login:{' '}
                    <span className='font-semibold'>
                      {last_login_date
                        ? moment(last_login_date).format('DD-MM-YYYY @hh:mm A')
                        : 'N/A'}
                    </span>
                  </span>
                </div>
              </div>

              <div className='flex flex-col-reverse md:flex-row items-center justify-between mt-3 gap-3'>
                <div className='flex flex-col justify-center gap-1 bg-gray-100 rounded-lg px-4 py-2 min-h-[150px] flex-1'>
                  <AssociateRequestInfo
                    icon={
                      <ReactCountryFlag
                        style={{ width: '1.5em', height: '1.42em' }}
                        countryCode={iso_code}
                        svg
                      />
                    }
                    details={
                      <span className='font-semibold text-legal-dark-green'>
                        {country_name}
                      </span>
                    }
                  />

                  <AssociateRequestInfo
                    icon={<FaLocationDot className='size-5' />}
                    details={
                      <>
                        From{' '}
                        <span className='font-semibold text-legal-dark-green'>
                          {address}, {city_name}, {province_name}, {postal_code}
                        </span>
                      </>
                    }
                  />

                  <AssociateRequestInfo
                    icon={<MdEmail className='size-5' />}
                    details={
                      <span className='font-semibold text-base-orange'>
                        {email}
                      </span>
                    }
                  />

                  <AssociateRequestInfo
                    icon={<IoCall className='size-5' />}
                    details={
                      <span className='font-semibold text-legal-dark-green'>
                        {`${isd_code}${phone}`}
                      </span>
                    }
                  />

                  {date_of_birth ? (
                    <AssociateRequestInfo
                      icon={<FaBirthdayCake className='size-5' />}
                      details={
                        <span className='font-semibold text-legal-dark-green'>
                          {moment(date_of_birth).format('Do MMMM, YYYY')}
                        </span>
                      }
                    />
                  ) : null}

                  {splittedLinkedinUrl.length ? (
                    <AssociateRequestInfo
                      icon={<FaLinkedin className='size-5' />}
                      details={
                        <a
                          href={linkedin_profile}
                          className='font-semibold text-base-orange hover:opacity-70 hover:underline duration-300 underline'
                          target='_blank'
                          rel='noreferrer'
                        >
                          {`/in/${
                            splittedLinkedinUrl
                              ? splittedLinkedinUrl[
                                  Number(splittedLinkedinUrl.length) - 1
                                ]
                                ? splittedLinkedinUrl[
                                    Number(splittedLinkedinUrl.length) - 1
                                  ]
                                : splittedLinkedinUrl[
                                    Number(splittedLinkedinUrl.length) - 2
                                  ]
                              : null
                          }`}
                        </a>
                      }
                    />
                  ) : null}
                </div>

                <div>
                  {profile_picture ? (
                    <div className='size-[150px] rounded-lg overflow-hidden'>
                      <img
                        className='w-full h-full object-cover'
                        src={showPicture(profile_picture)}
                        alt={`avatar of ${username}`}
                      />
                    </div>
                  ) : (
                    <div className='flex items-center justify-center bg-gray-100 size-[150px] rounded-lg'>
                      <FaUserAlt className='size-20 text-gray-300' />
                    </div>
                  )}
                </div>
              </div>

              {per_hour_rate && years_of_experience && user_expertise ? (
                <AssociateRequestExperience
                  fieldOfExpertise={
                    user_expertise
                      .map((expertise) => expertise.service_name)
                      .join(', ') || 'N/A'
                  }
                  perHourRate={per_hour_rate}
                  yearsOfExperience={years_of_experience}
                  businessRegNo={business_reg_no || 'N/A'}
                  hstNumber={hst_no || 'N/A'}
                  licenseNumber={license_number || 'N/A'}
                />
              ) : null}

              <AcademiaSectionWrapper sectionTitle={`Applicant's Bio`}>
                <p className='text-base-dark-blue'>{bio || 'N/A'}</p>
              </AcademiaSectionWrapper>

              {languages_spoken?.length ? (
                <AcademiaSectionWrapper sectionTitle={`Language expertise`}>
                  <div className='flex flex-wrap gap-2'>
                    {languages_spoken.map((lang) => (
                      <SkillBadge
                        key={lang.id}
                        badgeLabel={lang.name}
                        color='#388229'
                      />
                    ))}
                  </div>
                </AcademiaSectionWrapper>
              ) : null}

              {!isReadOnly ? (
                <div className='space-y-3'>
                  <div className='mt-5 grid md:grid-cols-2 gap-3'>
                    <AcademiaButton
                      title={
                        associate.status === 'resubmit' &&
                        !associate.is_resubmit
                          ? 'Resubmission request is already sent for this associate!'
                          : ''
                      }
                      type='button'
                      onClick={() => setRequestResubmission(true)}
                      variant='warning'
                      icon={<TfiReload className='size-6' />}
                      isDisabled={
                        associate.status === 'resubmit' &&
                        !associate.is_resubmit
                      }
                      fullWidth
                    >
                      Request Resubmission
                    </AcademiaButton>

                    <AcademiaButton
                      title={
                        associate.status === 'rejected'
                          ? 'This associate is already rejected!'
                          : ''
                      }
                      type='button'
                      onClick={() => setIsRejecting(true)}
                      variant='danger'
                      icon={<FaBan className='size-6' />}
                      isDisabled={associate.status === 'rejected'}
                      fullWidth
                    >
                      Reject Applicant
                    </AcademiaButton>
                  </div>

                  <AcademiaButton
                    title={
                      associate.status === 'resubmit' && !associate.is_resubmit
                        ? 'Can not approve an applicant while resubmission request is in progress'
                        : undefined
                    }
                    type='button'
                    onClick={() => setIsApproving(true)}
                    variant='success'
                    icon={<MdVerified className='size-6' />}
                    isDisabled={
                      associate.status === 'resubmit' && !associate.is_resubmit
                    }
                    fullWidth
                  >
                    Approve Applicant
                  </AcademiaButton>
                </div>
              ) : null}
            </div>
          </>
        ) : getUserProfileByIdHandler.isError ? (
          <p className='text-center text-base-red pt-2 text-lg font-medium'>
            No data found!! <br /> Please, Ask the applicant to reconfigure
            profile!!
          </p>
        ) : null}
      </ParentModal>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {requestResubmission ? (
          <ApplicationConsentModal
            handleModalClose={() => setRequestResubmission(false)}
            status='resubmit'
            mutationHandler={changeApplicantStatusHandler}
            subTitle={
              <>
                Are you sure you want to request resubmission of application for
                this associate?
              </>
            }
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {isApproving ? (
          <ApplicationConsentModal
            handleModalClose={() => setIsApproving(false)}
            status='approved'
            mutationHandler={changeApplicantStatusHandler}
            subTitle={<>Are you sure you want to approve this associate?</>}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {isRejecting ? (
          <ApplicationConsentModal
            handleModalClose={() => setIsRejecting(false)}
            status='rejected'
            mutationHandler={changeApplicantStatusHandler}
            subTitle={<>Are you sure you want to reject this associate?</>}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default AssociateRequestDetailsModal;
